import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Circle, Search } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../lib/api";
import { StatusBadge, ChannelBadge } from "../components/StatusBadge";

const STAGES = ["pending", "supplier_ordered", "shipped", "delivered"];
const STAGE_LABELS = {
  pending: "Awaiting payment",
  supplier_ordered: "Ordered from supplier",
  shipped: "Shipped",
  delivered: "Delivered",
};

export default function TrackOrderPage() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (on = orderNumber, em = email) => {
    if (!on || !em) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders/lookup", { order_number: on, email: em });
      setOrder(data.order);
    } catch (e) {
      setOrder(null);
      toast.error(formatApiError(e, "Order not found"));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (params.get("order") && params.get("email")) lookup(params.get("order"), params.get("email"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentStage = order ? STAGES.indexOf(order.fulfillment.status) : -1;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-8" data-testid="track-page">
      <p className="eyebrow mb-2">Order tracking</p>
      <h1 className="font-heading text-3xl font-bold text-white">Where's my order?</h1>
      <div className="card-surface mt-6 flex flex-col gap-3 p-5 sm:flex-row">
        <input data-testid="track-order-input" className="input-dark" placeholder="Order number (e.g. TD-100001)" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
        <input data-testid="track-email-input" className="input-dark" type="email" placeholder="Checkout email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="track-lookup-btn" onClick={() => lookup()} disabled={loading} className="btn-gold shrink-0 !px-5 !py-2.5">
          <Search size={16} /> {loading ? "Looking up…" : "Track"}
        </button>
      </div>

      {order && (
        <div className="card-surface mt-6 p-6" data-testid="track-result">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-mono text-lg font-bold text-amber-400" data-testid="track-order-number">{order.order_number}</p>
              <p className="text-xs text-slate-500">Placed {order.created_at.slice(0, 10)} · {fmt(order.total || order.subtotal)}</p>
            </div>
            <div className="flex items-center gap-2">
              <ChannelBadge channel={order.channel} />
              <StatusBadge status={order.status} testid="track-order-status" />
            </div>
          </div>

          <div className="mt-8 space-y-0">
            {STAGES.map((s, i) => {
              const done = i <= currentStage && order.payment_status === "paid";
              const hist = order.fulfillment.history?.find((h) => h.status === s);
              return (
                <div key={s} className="flex gap-4" data-testid={`track-stage-${s}`}>
                  <div className="flex flex-col items-center">
                    {done ? <CheckCircle2 size={20} className="text-amber-400" /> : <Circle size={20} className="text-slate-700" />}
                    {i < STAGES.length - 1 && <div className={`h-10 w-px ${i < currentStage ? "bg-amber-400/50" : "bg-white/10"}`} />}
                  </div>
                  <div className="pb-8">
                    <p className={`text-sm font-semibold ${done ? "text-white" : "text-slate-600"}`}>{STAGE_LABELS[s]}</p>
                    {hist && <p className="mt-0.5 text-xs text-slate-500">{hist.note}</p>}
                    {hist && <p className="text-[11px] text-slate-600">{hist.at.replace("T", " ").slice(0, 19)}</p>}
                    {s === "shipped" && order.fulfillment.tracking_number && (
                      <p className="mt-1 text-xs text-slate-400">
                        {order.fulfillment.carrier} · <span className="font-mono text-amber-300" data-testid="track-tracking-number">{order.fulfillment.tracking_number}</span>
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-white/10 pt-4">
            {order.items.map((it) => (
              <div key={it.product_id} className="flex items-center gap-3 py-2">
                <img src={it.image} alt={it.name} className="h-12 w-12 rounded-lg object-cover" />
                <p className="flex-1 text-sm text-slate-300">{it.name} × {it.qty}</p>
                <p className="text-sm font-semibold text-white">{fmt(it.price * it.qty)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
